import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { toast } from '../../../../../ui/toast';
import { AddCircleIcons, CircleCheckIcons, CircleXIcons, ClipBoardIcons, MapPinIcons, SquareCheckIcons, TestNoteIcons } from '../../../../../icons/icons';
import { Button } from '../../../../../ui';
import { saveFilter } from '../../../../../utils/api_service/JobOpenings';
import { useGetPreQualConfigQuery } from '../../../../../utils/api_service/preQualApi';
import { PreQualCondition, PreQualFilter, PreQualQuestion } from '../../../../../utils/types/JobOpenings';

interface PreQualFilterDrawerContentProps {
  jobOpeningId: string;
  prequal_filter: PreQualFilter | null;
  setPrequalFilter: (filter: PreQualFilter | null) => void;
  onApply: () => void;
  searchObject: any;
}

export const PreQualFilterDrawerContent: React.FC<PreQualFilterDrawerContentProps> = ({
  jobOpeningId,
  prequal_filter,
  setPrequalFilter,
  onApply,
  searchObject,
}) => {
  const { data: config, isLoading } = useGetPreQualConfigQuery(jobOpeningId);
  const [localConditions, setLocalConditions] = useState<PreQualCondition[]>([]);

  // Initialize all questions as conditions
  useEffect(() => {
    if (config?.questions) {
      const allQuestions = config.questions;
      const initialConditions = allQuestions.map((q) => {
        // Find existing condition if it exists in prequal_filter
        const existing = prequal_filter?.conditions.find(
          (c) => c.question_key === q.questionKey,
        );
        if (existing) return existing;

        // Initialize empty condition based on type
        const empty: PreQualCondition = { question_key: q.questionKey || '' };
        if (q.questionType === 'multiple-choice') {
          empty.choice = { values: [] };
        } else if (q.questionType === 'number') {
          empty.number = { min: undefined, max: undefined };
        } else if (q.questionType === 'location') {
          empty.location = { country: '' };
        }
        return empty;
      });
      setLocalConditions(initialConditions);
    }
  }, [config, prequal_filter]);

  const questions = config?.questions || [];

  const updateCondition = (
    index: number,
    updates: Partial<PreQualCondition>,
  ) => {
    const updated = [...localConditions];
    updated[index] = { ...updated[index], ...updates };
    setLocalConditions(updated);
  };

  const handleApply = async () => {
    // Clean up conditions to remove undefined/null fields
    const cleanedConditions = localConditions
      .map((condition) => {
        const cleaned: any = { question_key: condition.question_key };

        if (condition.choice) {
          const values = condition.choice.values.filter((v) => v !== '');
          if (values.length > 0) {
            cleaned.choice = { values };
          }
        } else if (condition.number) {
          const num: any = {};
          if (condition.number.min !== undefined)
            num.min = condition.number.min;
          if (condition.number.max !== undefined)
            num.max = condition.number.max;
          if (Object.keys(num).length > 0) {
            cleaned.number = num;
          }
        } else if (condition.location) {
          const loc: any = {};
          // Radius mode: all three fields required
          if (
            condition.location.lat !== undefined &&
            condition.location.lng !== undefined &&
            condition.location.radius_km !== undefined
          ) {
            loc.lat = condition.location.lat;
            loc.lng = condition.location.lng;
            loc.radius_km = condition.location.radius_km;
          }
          if (condition.location.city) loc.city = condition.location.city;
          if (condition.location.country)
            loc.country = condition.location.country;

          if (Object.keys(loc).length > 0) {
            cleaned.location = loc;
          }
        }
        return cleaned;
      })
      .filter((c) => c.choice || c.number || c.location);

    const newFilter =
      cleanedConditions.length === 0 ? null : { conditions: cleanedConditions };

    setPrequalFilter(newFilter);

    try {
      // Also save to backend
      const savePayload = {
        ...searchObject,
        prequal_filter: newFilter,
      };
      await saveFilter(jobOpeningId, savePayload);
      toast.success('Filters applied and saved');
    } catch (error) {
      console.error('Error saving filter:', error);
      toast.error('Filters applied but failed to save to database');
    }

    onApply();
  };

  const getQuestionIcon = (type: string) => {
    switch (type) {
      case 'multiple-choice':
        return <SquareCheckIcons className='w-4 h-4 text-emerald-500' />;
      case 'number':
        return <ClipBoardIcons className='w-4 h-4 text-blue-500' />;
      case 'location':
        return <MapPinIcons className='w-4 h-4 text-rose-500' />;
      default:
        return <TestNoteIcons className='w-4 h-4 text-slate-500' />;
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500'></div>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full bg-slate-50/30'>
      <div className='flex-1 overflow-y-auto p-6 space-y-6'>
        <div className=''>
          <div className='flex items-center justify-between mb-6 pb-4 border-b border-slate-100'>
            <h3 className='text-sm font-semibold text-slate-800'>
              Questionnaire Filters
            </h3>
            <span className='text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full font-medium'>
              { questions.length } Questions Available
            </span>
          </div>

          <div className='space-y-8'>
            { localConditions.map((condition, index) => {
              const question = questions.find(
                (q) => q.questionKey === condition.question_key,
              );
              if (!question) return null;

              return (
                <div key={condition.question_key} className='relative group'>
                  <div className='flex items-center gap-2 mb-3'>
                    <div className='p-1.5 bg-slate-50 rounded-lg group-focus-within:bg-emerald-50 transition-colors'>
                      { getQuestionIcon(question.questionType) }
                    </div>
                    <label className='text-xs font-semibold text-slate-700 leading-snug'>
                      { question.question }
                    </label>
                  </div>

                  <div className='pl-9 space-y-4'>
                    { /* Choice Filter */ }
                    { question.questionType === 'multiple-choice' && (
                      <div className='space-y-2'>
                        <Select
                            isMulti
                            placeholder='Select allowed answers...'
                            options={question.choices?.map((c) => ({
                              value: c,
                              label: c,
                            }))}
                            value={condition.choice?.values.map((v) => ({
                              value: v,
                              label: v,
                            }))}
                            onChange={(options: any) =>
                              updateCondition(index, {
                                choice: { values: (options || []).map((o: any) => o.value) },
                              })
                            }
                            className='text-sm'
                            styles={{
                              control: (base: any) => ({
                                ...base,
                                borderRadius: '10px',
                                borderColor: '#e2e8f0',
                                '&:hover': { borderColor: '#cbd5e1' },
                              }),
                            }}
                        />
                      </div>
                    ) }

                    { /* Number Filter */ }
                    { question.questionType === 'number' && (
                      <div className='grid grid-cols-2 gap-4'>
                        <div>
                          <label className='block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5'>
                            Min Value
                          </label>
                          <input
                              type='number'
                              className='w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
                              value={condition.number?.min ?? ''}
                              onChange={(e) =>
                                updateCondition(index, {
                                  number: {
                                    ...condition.number,
                                    min: e.target.value
                                      ? Number(e.target.value)
                                      : undefined,
                                  },
                                })
                              }
                              placeholder='Any'
                          />
                        </div>
                        <div>
                          <label className='block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5'>
                            Max Value
                          </label>
                          <input
                              type='number'
                              className='w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
                              value={condition.number?.max ?? ''}
                              onChange={(e) =>
                                updateCondition(index, {
                                  number: {
                                    ...condition.number,
                                    max: e.target.value
                                      ? Number(e.target.value)
                                      : undefined,
                                  },
                                })
                              }
                              placeholder='Any'
                          />
                        </div>
                      </div>
                    ) }

                    { /* Location Filter */ }
                    { question.questionType === 'location' && (
                      <div className='space-y-4'>
                        <div className='grid grid-cols-2 gap-4'>
                          <div>
                            <label className='block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5'>
                              Country
                            </label>
                            <input
                                type='text'
                                className='w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20'
                                value={condition.location?.country || ''}
                                onChange={(e) =>
                                  updateCondition(index, {
                                    location: {
                                      ...condition.location,
                                      country: e.target.value,
                                    },
                                  })
                                }
                                placeholder='e.g. Australia'
                            />
                          </div>
                          <div>
                            <label className='block text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5'>
                              City (Optional)
                            </label>
                            <input
                                type='text'
                                className='w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500/20'
                                value={condition.location?.city || ''}
                                onChange={(e) =>
                                  updateCondition(index, {
                                    location: {
                                      ...condition.location,
                                      city: e.target.value,
                                    },
                                  })
                                }
                                placeholder='e.g. Sydney'
                            />
                          </div>
                        </div>

                        <div className='pt-2 border-t border-slate-100'>
                          <p className='text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-2'>
                            OR Radius Mode
                          </p>
                          <div className='grid grid-cols-3 gap-3'>
                            <div>
                              <label className='block text-[8px] text-slate-400 mb-1'>
                                Lat
                              </label>
                              <input
                                  type='number'
                                  className='w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded-lg'
                                  value={condition.location?.lat ?? ''}
                                  onChange={(e) =>
                                    updateCondition(index, {
                                      location: {
                                        ...condition.location,
                                        lat: e.target.value
                                          ? Number(e.target.value)
                                          : undefined,
                                      },
                                    })
                                  }
                              />
                            </div>
                            <div>
                              <label className='block text-[8px] text-slate-400 mb-1'>
                                Lng
                              </label>
                              <input
                                  type='number'
                                  className='w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded-lg'
                                  value={condition.location?.lng ?? ''}
                                  onChange={(e) =>
                                    updateCondition(index, {
                                      location: {
                                        ...condition.location,
                                        lng: e.target.value
                                          ? Number(e.target.value)
                                          : undefined,
                                      },
                                    })
                                  }
                              />
                            </div>
                            <div>
                              <label className='block text-[8px] text-slate-400 mb-1'>
                                Radius (km)
                              </label>
                              <input
                                  type='number'
                                  className='w-full px-2 py-1.5 text-xs bg-white border border-slate-200 rounded-lg'
                                  value={condition.location?.radius_km ?? ''}
                                  onChange={(e) =>
                                    updateCondition(index, {
                                      location: {
                                        ...condition.location,
                                        radius_km: e.target.value
                                          ? Number(e.target.value)
                                          : undefined,
                                      },
                                    })
                                  }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ) }

                    { question.questionType === 'text' && (
                      <div className='p-3 bg-amber-50 rounded-lg border border-amber-100 flex gap-2'>
                        <div className='w-1.5 h-1.5 rounded-full bg-amber-400 mt-1' />
                        <p className='text-[10px] text-amber-700 italic'>
                          Text matching is not supported in the current pre-qual
                          filter spec.
                        </p>
                      </div>
                    ) }
                  </div>
                </div>
              );
            }) }
          </div>
        </div>
      </div>

      <div className='p-6 bg-white border-t border-slate-200 flex items-center justify-between gap-4'>
        <button
            onClick={() => {
              const reset = localConditions.map((c) => {
                const r = { ...c };
                if (r.choice) r.choice = { values: [] };
                if (r.number) r.number = { min: undefined, max: undefined };
                if (r.location) r.location = { country: '', city: '' };
                return r;
              });
              setLocalConditions(reset);
              setPrequalFilter(null);
            }}
            className='text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors'
        >
          Reset All
        </button>
        <div className='flex gap-3'>
          <Button label='Cancel' onClick={onApply} variant='secondary' />
          <Button
              label='Apply Filters'
              onClick={handleApply}
              variant='primary'
          />
        </div>
      </div>
    </div>
  );
};
